// --- React
import React, { useCallback } from 'react';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
// --- Hooks
import { useDialogsActions } from '../hooks';
import { useDialogsForm } from '../forms';

/** * DeleteForm Component
 *
 * This component is responsible for rendering the delete confirmation message.
 * It displays a message to confirm the deletion action.
 *
 * @component
 * @param {Object} msg - The message object containing confirmation text.
 * @returns {JSX.Element} The rendered DeleteForm component.
 */
const DeleteForm = ({ msg }) => (
    <div className="flexBox">
        <Typography variant="T30">{msg[0]}</Typography>
        <Typography variant="T20">{msg[1]}</Typography>
    </div>
);

/**
 * useDeleteDialog Hook
 *
 * This hook provides functionality to open a delete confirmation dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openDeleteDialog function.
 */
export const useDeleteDialog = () => {
    const { openDialog, closeDialog } = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = useCallback(
        (onSubmit) => {
            onSubmit(form.state.formData);
            form.reset();
            closeDialog();
        },
        [form, closeDialog],
    );

    // Function to open the delete confirmation dialog
    const openDeleteDialog = useCallback(
        (label, msg, onConfirm) => {
            openDialog({
                title: 'Confirm Deletion',
                content: <DeleteForm msg={msg} />,
                closeAction: true,
                primaryAction: {
                    label,
                    action: () => handleSubmit(onConfirm),
                },
            });
        },
        [openDialog, handleSubmit],
    );

    return { openDeleteDialog };
};
